/**
 * AuthService - Handles authentication operations
 */

import httpService from '../../../core/services/HttpService';
import LocalStorageService from '../../../core/services/LocalStorageService';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password2: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface RegisterResponse {
  access: string;
  refresh: string;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface RefreshTokenResponse {
  access: string;
}

class AuthService {
  /**
   * Login user with credentials
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const response = await httpService.post<AuthTokens>('/api/admin/token/', credentials);

    // Store tokens in localStorage
    LocalStorageService.setAccessToken(response.data.access);
    LocalStorageService.setRefreshToken(response.data.refresh);

    return response.data;
  }

  /**
   * Register new user and automatically authenticate
   */
  async register(userData: RegisterData): Promise<RegisterResponse> {
    const response = await httpService.post<RegisterResponse>('/api/register/', userData);

    // Store tokens in localStorage (same as login)
    LocalStorageService.setAccessToken(response.data.access);
    LocalStorageService.setRefreshToken(response.data.refresh);

    return response.data;
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await httpService.post<RefreshTokenResponse>('/api/admin/token/refresh/', {
      refresh: refreshToken
    });

    // Update stored access token
    LocalStorageService.setAccessToken(response.data.access);

    return response.data;
  }

  /**
   * Logout user by clearing tokens
   */
  logout(): void {
    LocalStorageService.clearAuthData();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = LocalStorageService.getAccessToken();
    return !!token;
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return LocalStorageService.getAccessToken();
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return LocalStorageService.getRefreshToken();
  }

  /**
   * Store authentication error for display on login page
   */
  storeAuthError(error: string): void {
    LocalStorageService.setAuthError(error);
  }

  /**
   * Clear stored authentication error
   */
  clearAuthError(): void {
    LocalStorageService.removeAuthError();
  }

  /**
   * Get stored authentication error
   */
  getAuthError(): string | null {
    return LocalStorageService.getAuthError();
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService; 
